/**
 * @fileoverview Task automations section (MOR-736)
 *
 * Lists the automation rules that target the open task, each with a link back
 * to where the rule can be edited — its workflow (`?tab=automations`) for
 * workflow-scoped rules, or the org Automations page for org-wide rules — so
 * practitioners can see and adjust automation impact straight from the task
 * side panel. Mirrors the ⚡ indicator on the task list / board.
 *
 * Job-scoped rules (attached to an ad-hoc job task or auto-calculated
 * statutory event) are created and edited inline here via
 * {@link AutomationRuleDialog}: this panel is their primary management surface.
 * Persisted tasks and statutory events (identified by `code`) always show an
 * "Add automation" button.
 *
 * Matching reuses `rulesTargetingTaskTitle` (title substring, disabled rules
 * excluded) so this panel stays consistent with the list, board, and workflow
 * editor. Workflow-scoped rules only appear on jobs the workflow was applied to.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { ExternalLink, Settings2, Zap } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { CalendarEventRow } from "@/calendar/eventAdapter";
import { rulesTargetingTaskTitle } from "@/components/settings/automations/automation-config";
import { AutomationRuleSummary } from "@/components/settings/automations/AutomationRuleSummary";
import {
  AutomationRuleDialog,
  type EditableRule,
} from "@/components/settings/automations/AutomationRuleDialog";
import { Button } from "@/ui/primitives";

export function TaskAutomationsSection({
  event,
  onNavigate,
}: {
  event: CalendarEventRow;
  /** Called just before navigating away, so the host can close the panel. */
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  // When set, the rule builder opens in edit mode for this existing job rule;
  // when null it opens in "add" mode scoped to this job task.
  const [editingRule, setEditingRule] = useState<EditableRule | null>(null);

  const rules = useQuery(api.automations.rules.listByOrg, {
    orgId: event.orgId,
  });
  const applied = useQuery(api.automations.rules.listAppliedTemplates, {
    orgId: event.orgId,
    jobIds: [event.jobId],
  });

  const appliedTemplateIds = useMemo(() => {
    const set = new Set<string>();
    for (const a of applied ?? []) set.add(String(a.templateId));
    return set;
  }, [applied]);

  const matched = useMemo(() => {
    if (!rules) return [];
    const applicable = rules.filter((r) => {
      // Job-scoped rules only fire on their own job.
      if (r.jobId) return r.jobId === event.jobId;
      // Workflow-scoped rules only fire on jobs the workflow was applied to.
      if (r.templateId) return appliedTemplateIds.has(String(r.templateId));
      return true;
    });
    return rulesTargetingTaskTitle(applicable, event.title);
  }, [rules, appliedTemplateIds, event.jobId, event.title]);

  // Job-scoped automations attach by job + title, not row id. Persisted tasks
  // and auto-calculated statutory events (stable `code` + derived title) are
  // both valid targets — marking complete materialises derived rows first, then
  // fires task.status_changed / task.completed with the correct title.
  const canManageJobAutomations = Boolean(event.persistedId || event.code);
  const jobMatches = useMemo(() => matched.filter((r) => r.jobId), [matched]);

  // Rows with no stable identity and no matches have nothing to show.
  if (matched.length === 0 && !canManageJobAutomations) return null;

  const openAddAutomation = () => {
    setEditingRule(null);
    setDialogOpen(true);
  };

  const openRule = (rule: (typeof matched)[number]) => {
    if (rule.jobId) {
      // Job-scoped rules are edited inline here.
      setEditingRule(rule);
      setDialogOpen(true);
      return;
    }
    onNavigate?.();
    if (rule.templateId) {
      void navigate({
        to: "/dashboard/workflows/$workflowId",
        params: { workflowId: rule.templateId },
        search: { tab: "automations" as const },
      });
    } else {
      void navigate({ to: "/dashboard/automations" });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
          Automations
          {matched.length > 0 && (
            <span className="tabular-nums text-muted-foreground">
              {matched.length}
            </span>
          )}
        </h4>
        {canManageJobAutomations &&
          (jobMatches.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 gap-1 text-xs"
              title="Manage this task's automation"
              onClick={() => openRule(jobMatches[0])}
            >
              <Settings2 className="size-3.5" /> Manage automation
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 gap-1 text-xs"
              title="Add an automation for this task"
              onClick={openAddAutomation}
            >
              <Zap className="size-3.5 text-amber-500" /> Add automation
            </Button>
          ))}
      </div>
      {matched.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {matched.length === 1
            ? "This automation runs on this task. Open it to view or adjust it."
            : "These automations run on this task. Open one to view or adjust it."}
        </p>
      )}
      {matched.length > 0 && (
        <div className="mt-2 space-y-2">
          {matched.map((rule) => (
            <div
              key={rule._id}
              role="button"
              tabIndex={0}
              onClick={() => openRule(rule)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openRule(rule);
                }
              }}
              className="group flex w-full cursor-pointer items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-left transition-colors hover:border-amber-300 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-amber-900/50 dark:bg-amber-950/30 dark:hover:bg-amber-950/50"
            >
              <Zap className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0 flex-1">
                <span className="truncate text-sm font-medium text-foreground">
                  {rule.name}
                </span>
                <AutomationRuleSummary
                  orgId={event.orgId}
                  trigger={rule.trigger}
                  actions={rule.actions}
                  className="mt-0.5"
                />
                <span className="mt-0.5 block text-[11px] text-muted-foreground/80">
                  {rule.jobId
                    ? "This job only"
                    : rule.templateName
                      ? `Workflow: ${rule.templateName}`
                      : "Applies to every job"}
                </span>
              </div>
              <ExternalLink className="mt-0.5 size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          ))}
        </div>
      )}

      {/* Job-scoped rule builder — pre-fills the task title so the user
          doesn't retype it, and scopes the new rule to this job (MOR-736). */}
      {canManageJobAutomations && dialogOpen && (
        <AutomationRuleDialog
          orgId={event.orgId}
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingRule(null);
          }}
          initial={editingRule ?? undefined}
          jobId={event.jobId}
          jobName={event.jobName}
          defaultTaskTitleContains={event.title}
        />
      )}
    </div>
  );
}
